"use client";

import {
  ModalType,
  PriceType,
  returnProductTypeBasedOnComponentType,
} from "@/sections/BaliBanner/BaliPrice";
import OrnamentSVG from "@/public/assets/images/ornament.svg";
import { ProductStatusType, ProductType } from "@/types/Products";
import Button from "../Button";
import Text from "../Text";
import { transformText } from "@/utils/transformText";
import GiftIcon from "@/public/assets/images/purple-gift.svg";
import { useState } from "react";
import dynamic from "next/dynamic";
import { AnimatePresence } from "framer-motion";
import TextBlock from "../TextBlock";
import GradientLightText from "../GradientLightText";
import CustomImage from "../CustomImage";
import CloseIcon from "@/public/assets/images/X.svg";

const DynamicModal = dynamic(() => import("@/components/Modal"), {
  ssr: false,
});

type BaliPriceBoxProps = {
  component: PriceType;
  product: ProductType;
  isHighlighted?: boolean;
  modal?: ModalType;
  badge?: string;
};

type ProductInBasketTypeType =
  | "pro"
  | "upgrade"
  | "basic"
  | "eventTicket"
  | "eventBooking"
  | "eventDoubleTicket"
  | "eventDoubleBooking"
  | "marathon"
  | "bundle"
  | "astrology"
  | "lesson";

export const returnProductBasketTypeBasedOnComponentType = (
  type:
    | "singleTicket"
    | "singleReservation"
    | "doubleTicket"
    | "doubleReservation"
): ProductInBasketTypeType | undefined => {
  switch (type) {
    case "singleTicket":
      return "eventTicket";
    case "singleReservation":
      return "eventBooking";
    case "doubleTicket":
      return "eventDoubleTicket";
    case "doubleReservation":
      return "eventDoubleBooking";
  }
};

const BaliPriceBox = ({
  component,
  product,
  modal,
  badge,
  isHighlighted,
}: BaliPriceBoxProps) => {
  const [isModalActive, setIsModalActive] = useState(false);
  const purchasedQuery =
    product.status === ProductStatusType.Active && product
      ? `?product={"type":"${returnProductBasketTypeBasedOnComponentType(
          component.type
        )}","product_id":${product.id},"course_id":0,"price":${
          product.price_eur
        },"price_rub":${product.price_rub},"isDiscount":false}`
      : "";
  return (
    <>
      <div
        className={`bali-price-box ${
          isHighlighted ? "bali-price-box--highlighted" : ""
        }`.trim()}
      >
        {component.badge ? (
          <div className="bali-price-box__badge">
            <p className="bali-price-box__badge-text">
              {transformText(component.badge, {
                span: (chunk) => (
                  <span className="bali-price-box__badge-span">{chunk}</span>
                ),
              })}
            </p>
          </div>
        ) : null}
        <div className="bali-price-box__holder">
          <div className="bali-price-box__content">
            <div className="bali-price-box__ornament">
              <OrnamentSVG />
            </div>
            <p
              className={`bali-price-box__price text-center noto-serif weight-700 ${
                isHighlighted ? "light-gradient" : ""
              }`.trim()}
            >
              {product.price_eur}€
              {component.priceFollowText ? (
                <Text
                  element="span"
                  elementStyle="body"
                  className="bali-price-box__price-span"
                >
                  {component.priceFollowText}
                </Text>
              ) : null}
            </p>
            <div className="bali-price-box__text-with-button">
              <div className="bali-price-box__text-holder">
                <Text
                  element="h3"
                  elementStyle="subtitle"
                  fontFamily="noto-serif"
                  className={`center text-center bali-price-box__title weight-700 ${
                    isHighlighted ? "light-gradient" : ""
                  }`.trim()}
                >
                  {component.title}
                </Text>
                <Text
                  element="p"
                  elementStyle="body-secondary"
                  fontFamily="noto-serif"
                  className={`weight-700 center text-center bali-price-box__text ${
                    isHighlighted ? "light-gradient" : ""
                  }`.trim()}
                >
                  {component.text}
                </Text>
              </div>
              <div className="bali-price-box__buttons">
                {purchasedQuery &&
                product.status === ProductStatusType.Active ? (
                  <Button
                    type="primary"
                    isLink
                    isTargetBlank
                    href={
                      process.env.NEXT_PUBLIC_PORTAL_URL +
                      `/portal/account/basket${purchasedQuery}`
                    }
                  >
                    {component.button.text}
                  </Button>
                ) : null}
                {component.detailsButton ? (
                  <button
                    onClick={() => {
                      setIsModalActive(true);
                      document.body.classList.add("no-scroll");
                    }}
                    className="bali-price-box__details noto-serif weight-700"
                  >
                    <GiftIcon /> {component.detailsButton.text}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <AnimatePresence initial={false} onExitComplete={() => null}>
        {isModalActive && modal ? (
          <DynamicModal
            isDarkerFull
            handleClose={() => {
              setIsModalActive(false);
              document.body.classList.remove("no-scroll");
            }}
          >
            <div className="bali-price-box__modal">
              <button
                onClick={() => {
                  setIsModalActive(false);
                  document.body.classList.remove("no-scroll");
                }}
                className="bali-price-box__modal-close"
              >
                <CloseIcon />
              </button>
              <CustomImage
                alt="Badge"
                src="/assets/images/gift-visual.png"
                srcTablet="/assets/images/gift-visual.png"
                srcMobile="/assets/images/gift-visual.png"
                className={`bali-price-box__modal-badge ${
                  modal.isReservation
                    ? "bali-price-box__modal-badge--reservation"
                    : ""
                }`.trim()}
              />
              {modal.badge ? (
                <div className="bali-price-box__badge">
                  <p className="bali-price-box__badge-text">
                    {transformText(modal.badge, {
                      span: (chunk) => (
                        <span className="bali-price-box__badge-span">
                          {chunk}
                        </span>
                      ),
                    })}
                  </p>
                </div>
              ) : null}
              <div className="bali-price-box__modal-holder">
                <div className="bali-price-box__modal-left">
                  <TextBlock>
                    <Text
                      element="p"
                      elementStyle="body"
                      fontFamily="noto-serif"
                      className="weight-700 bali-price-box__modal-subtitle"
                    >
                      {transformText(modal.subtitle, {
                        gradient: (chunk) => (
                          <GradientLightText element="span">
                            {chunk}
                          </GradientLightText>
                        ),
                      })}
                    </Text>
                    <Text
                      element="h3"
                      elementStyle="h1"
                      className="bali-price-box__modal-title"
                    >
                      {transformText(modal.title, {
                        gradient: (chunk) => (
                          <GradientLightText element="span">
                            {chunk}
                          </GradientLightText>
                        ),
                      })}
                    </Text>
                    <Text
                      element="p"
                      elementStyle="body"
                      fontFamily="noto-serif"
                      className="weight-700 bali-price-box__modal-text"
                    >
                      {modal.text}
                    </Text>
                    <Text
                      element="p"
                      elementStyle="subtitle"
                      fontFamily="noto-serif"
                      className="weight-700 bali-price-box__modal-list-title"
                    >
                      {modal.listTitle}
                    </Text>
                    <ul className="bali-price-box__modal-list">
                      {modal.items.map((item) => (
                        <Text
                          key={item}
                          element="li"
                          elementStyle="body"
                          fontFamily="noto-serif"
                          className="weight-700 bali-price-box__modal-item"
                        >
                          {item}
                        </Text>
                      ))}
                    </ul>
                  </TextBlock>
                </div>
                <div className="bali-price-box__modal-right">
                  <CustomImage
                    alt={modal.image.alt}
                    src={modal.image.src.desktop}
                    srcTablet={modal.image.src.tablet}
                    srcMobile={modal.image.src.mobile}
                  />
                  <div className="bali-price-box__modal-quote-and-button">
                    <Text element="p" elementStyle="h2">
                      {modal.quote}
                    </Text>
                    <div>
                      <Text element="p" elementStyle="h1">
                        {transformText(modal.price, {
                          gradient: (chunk) => (
                            <GradientLightText
                              element="span"
                              classNames="light-gradient--no-shadow"
                            >
                              {chunk}
                            </GradientLightText>
                          ),
                        })}
                      </Text>
                      {purchasedQuery &&
                      product.status === ProductStatusType.Active ? (
                        <Button
                          type="primary"
                          isLink
                          isTargetBlank
                          href={
                            process.env.NEXT_PUBLIC_PORTAL_URL +
                            `/portal/account/basket${purchasedQuery}`
                          }
                        >
                          {modal.button.text}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DynamicModal>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default BaliPriceBox;
